import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD313Query } from './hooks/useD313Query';
import type { ReportRow, UnitSummary, WarehouseUnitSummary } from '../../services/d313Api';
import './D313Form.css';

const D313Form: React.FC = () => {
  const { loading, data, unitSummary, warehouseUnitSummary, queried, noData, runQuery, reset } = useD313Query();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [orderNoFrom, setOrderNoFrom] = useState('');
  const [orderNoTo, setOrderNoTo] = useState('');
  const [warehouseFrom, setWarehouseFrom] = useState('');
  const [warehouseTo, setWarehouseTo] = useState('');

  const handleQuery = () => {
    runQuery({
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      order_no_from: orderNoFrom.trim() || undefined,
      order_no_to: orderNoTo.trim() || undefined,
      warehouse_from: warehouseFrom.trim() || undefined,
      warehouse_to: warehouseTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setOrderNoFrom('');
    setOrderNoTo('');
    setWarehouseFrom('');
    setWarehouseTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>入布明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>入布明細表</h2>
<table><thead><tr><th>入庫單號</th><th>入庫日期</th><th>品號</th><th>庫位</th><th>庫位名稱</th><th>序號</th><th>單位</th><th>顏色</th><th>數量</th><th>備註</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD120_01}</td><td>${r.FD120_11}</td><td>${r.FD120_12}</td><td>${r.FD120_14}</td><td>${r.warehouse_name}</td><td>${r.FD121_02}</td><td>${r.FD121_UNIT}</td><td>${r.FD120_COLN || r.FD120_COL}</td><td class="right">${r.FD120_QTY}</td><td>${r.FD120_TB || ''}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '入庫單號', dataIndex: 'FD120_01', key: 'FD120_01', width: 110 },
    { title: '入庫日期', dataIndex: 'FD120_11', key: 'FD120_11', width: 100 },
    { title: '品號', dataIndex: 'FD120_12', key: 'FD120_12', width: 120 },
    { title: '庫位', dataIndex: 'FD120_14', key: 'FD120_14', width: 80 },
    { title: '庫位名稱', dataIndex: 'warehouse_name', key: 'warehouse_name', ellipsis: true, width: 120 },
    { title: '序號', dataIndex: 'FD121_02', key: 'FD121_02', width: 60, align: 'center' as const },
    { title: '單位', dataIndex: 'FD121_UNIT', key: 'FD121_UNIT', width: 60 },
    { title: '顏色', key: 'color', width: 140, render: (_: unknown, r: ReportRow) => r.FD120_COLN || r.FD120_COL || '' },
    { title: '數量', dataIndex: 'FD120_QTY', key: 'FD120_QTY', width: 100, align: 'right' as const,
      render: (v: number) => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    { title: '備註', dataIndex: 'FD120_TB', key: 'FD120_TB', ellipsis: true },
  ];

  const whSummaryColumns: ColumnsType<WarehouseUnitSummary> = [
    { title: '庫位', dataIndex: 'warehouse_code', key: 'warehouse_code', width: 80 },
    { title: '庫位名稱', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 120 },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const,
      render: (v: number) => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
  ];

  const unitSummaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const,
      render: (v: number) => v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D313} action="query">
      <div className="d313-container">
        <div className="d313-header">
          <h2>入布明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              {filterLabel('入庫單號起')}
              <Input value={orderNoFrom} onChange={e => setOrderNoFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫單號迄')}
              <Input value={orderNoTo} onChange={e => setOrderNoTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('庫位代號起')}
              <Input value={warehouseFrom} onChange={e => setWarehouseFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('庫位代號迄')}
              <Input value={warehouseTo} onChange={e => setWarehouseTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FD120_01}-${r.FD121_02}-${r.FD122_03}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }}
              style={{ marginBottom: 16 }}
            />
            {warehouseUnitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>庫位單位小計</div>
                <Table<WarehouseUnitSummary>
                  columns={whSummaryColumns}
                  dataSource={warehouseUnitSummary}
                  rowKey={(r) => `${r.warehouse_code}-${r.unit}`}
                  size="small"
                  pagination={false}
                  scroll={{ x: 500 }}
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位總計</div>
                <Table<UnitSummary>
                  columns={unitSummaryColumns}
                  dataSource={unitSummary}
                  rowKey="unit"
                  size="small"
                  pagination={false}
                  scroll={{ x: 300 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default D313Form;
